import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Plane, Headphones, BedDouble, Loader2, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "@/components/landing/Footer";
import CreditCardMock from "@/components/landing/CreditCardMock";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { trackMetaPixelCompleteRegistration, trackMetaPixelIssueCardClick } from "@/lib/metaPixel";
import { useLanguage } from "@/i18n/LanguageContext";

interface CardTier {
  id: string;
  name: string;
  price: string;
  description: string;
  requirement?: string;
  perks: { icon: React.ComponentType<{ className?: string }>; label: string; color: string }[];
  popular?: boolean;
  mockVariant: "silver" | "black" | "metal";
  lastDigits: string;
  cardLabel: string;
}

const IssueCardPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [selected, setSelected] = useState("blue");
  const [issueState, setIssueState] = useState<"idle" | "loading" | "success">("idle");

  const handleIssue = () => {
    trackMetaPixelIssueCardClick("issue-card-page");
    setIssueState("loading");
    setTimeout(() => {
      trackMetaPixelCompleteRegistration();
      setIssueState("idle");
      const prefix = language === "en" ? "" : `/${language}`;
      navigate(`${prefix}/success`);
    }, 1500);
  };

  const handleClose = () => {
    setIssueState("idle");
  };

  const commonPerks = [
    { icon: Sparkles, label: "Claude Pro", color: "text-orange-500" },
    { icon: Plane, label: "Airport lounges", color: "text-primary" },
    { icon: Headphones, label: "24/7 concierge", color: "text-primary" },
    { icon: BedDouble, label: "Hotel upgrades", color: "text-primary" },
  ];

  const tiers: CardTier[] = [
    {
      id: "white",
      name: "White",
      price: "Free",
      description: "Earn up to 5% back in BTC, ETH, or stablecoins on every purchase.",
      perks: commonPerks,
      mockVariant: "silver",
      lastDigits: "0366",
      cardLabel: "Virtual",
    },
    {
      id: "blue",
      name: "Blue",
      price: "Free",
      description: "Earn up to 5% back in BTC, ETH, or stablecoins on every purchase.",
      popular: true,
      perks: commonPerks,
      mockVariant: "black",
      lastDigits: "0452",
      cardLabel: "Virtual",
    },
    {
      id: "metal",
      name: "Metal",
      price: "$20K+ portfolio",
      description: "Hold $20,000 or more in your portfolio and receive the physical metal card.",
      requirement: "$20K+",
      perks: commonPerks,
      mockVariant: "metal",
      lastDigits: "9010",
      cardLabel: "Metal",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur-xl">
        <div className="container flex h-20 items-center justify-between">
          <Link to="/" className="flex shrink-0 items-center" aria-label="Trust Wallet homepage">
            <img src="/trust-assets/wallets/trust-wallet-logo.png" alt="Trust Wallet" className="h-[2.4rem] w-auto md:h-[2.7rem]" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-2xl bg-foreground px-6 py-3 text-sm font-semibold text-background transition-transform hover:-translate-y-0.5"
          >
            Back to Home
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <main className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="text-primary font-semibold text-sm tracking-widest uppercase">
              CARD
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mt-3 mb-4">
              Choose a product
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Pick the card that fits your lifestyle. Instant issuance, no KYC required.
            </p>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {tiers.map((tier, i) => {
              const isSelected = selected === tier.id;
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.6, ease: "easeOut" }}
                  onClick={() => setSelected(tier.id)}
                  className={`group relative rounded-3xl border-2 cursor-pointer transition-all duration-300 overflow-hidden bg-card ${
                    isSelected
                      ? "border-primary shadow-2xl shadow-primary/20 scale-[1.02]"
                      : "border-border/50 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10"
                  }`}
                >
                  {/* Popular badge */}
                  {tier.popular && (
                    <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                      <span className="bg-primary text-primary-foreground text-xs font-bold px-5 py-1.5 rounded-b-xl shadow-lg shadow-primary/30">
                        Popular
                      </span>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Selection indicator */}
                    <div className="absolute top-5 right-5 z-10">
                      <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        isSelected
                          ? "bg-primary border-primary shadow-lg shadow-primary/40"
                          : "border-muted-foreground/20 bg-background/80"
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                      </div>
                    </div>

                    {/* Card visual */}
                    <div className="mb-6">
                      <CreditCardMock
                        variant={tier.mockVariant}
                        label={tier.cardLabel}
                        lastDigits={tier.lastDigits}
                        cardholder="Card Holder"
                        expires="12/28"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-foreground">{tier.name}</h3>
                      {tier.requirement && (
                        <span className="text-xs font-bold text-primary">{tier.requirement}</span>
                      )}
                    </div>
                    <p className="text-primary font-bold text-lg mt-1">{tier.price}</p>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed min-h-[3.5rem]">
                      {tier.description}
                    </p>

                    {/* Divider */}
                    <div className="h-px bg-border/50 my-5" />

                    {/* Perks */}
                    <div>
                      <p className="text-[10px] text-muted-foreground/70 uppercase tracking-[0.15em] font-semibold mb-3">
                        INCLUDED PERKS
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {tier.perks.map(({ icon: Icon, label, color }) => (
                          <div
                            key={label}
                            className="flex items-center gap-2 rounded-xl border border-border/50 bg-muted/30 px-3 py-2"
                          >
                            <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                            <span className="text-xs font-medium text-foreground truncate">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={handleIssue}
              className="inline-flex items-center gap-2 rounded-2xl bg-primary px-12 py-4 text-lg font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
            >
              Issue Card
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Issue Modal */}
      <Dialog open={issueState !== "idle"} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent className="sm:max-w-[420px] p-0 gap-0 rounded-2xl border-border overflow-hidden [&>button.absolute]:hidden">
          <DialogTitle className="sr-only">Card Issuance</DialogTitle>
          <div className="p-8">
            <AnimatePresence mode="wait">
              {issueState === "loading" && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center text-center py-8"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mb-6"
                  >
                    <Loader2 className="w-16 h-16 text-primary" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    Issuing your card...
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Your card is being issued. This will only take a moment.
                  </p>
                  <div className="flex gap-1 mt-4">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 rounded-full bg-primary"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {issueState === "success" && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className="flex flex-col items-center text-center py-8"
                >
                  <motion.div
                    className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-5"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
                  >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      🎉 Congratulations!
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-xs mb-6">
                      Your card has been issued and will appear in your wallet within 5–15 minutes.
                    </p>
                    <button
                      onClick={handleClose}
                      className="w-full inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
                    >
                      Done
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IssueCardPage;
